import Button from '@axa-japan/design-system-react/Button';
import Heading from '@axa-japan/design-system-react/Heading';
import { AddIcon } from '@axa-japan/design-system-react/Icons';
import Message from '@axa-japan/design-system-react/Message';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import { useState } from 'react';
import { getFileName } from '../../utils/documentUtil';
import { FILE_DEFAULT_NAME, MAX_FILES_PER_DOCUMENT } from '../../utils/fileUtils';
import { DocumentType, FileType } from '../../utils/formUtils';
import { renderCustomTags } from '../../utils/renderTags';
import ErrorMessage from '../ErrorMessage';
import FileUpload from './FileUpload';

type DocumentUploadProps = {
  document: DocumentType;
  header?: string;
  onFileChange: (files: FileType[]) => void;
  hasError?: boolean;
  receptionNo: string;
};

const DocumentUpload = ({ document, header, onFileChange, hasError, receptionNo }: DocumentUploadProps) => {
  const [warningModalContent, setWarningModalContent] = useState('');

  const handleFileChange = (index: number, fileId: string, file?: File) => {
    const files = [...document.files];
    files[index] = { fileId: fileId, content: file };
    onFileChange(files);
  };

  const handleFileAdd = () => {
    const exceededMaxFiles = document.files.length >= MAX_FILES_PER_DOCUMENT;
    const hasUnfilledFile = document.files.some((file: FileType) => !file.content || getFileName(file) === FILE_DEFAULT_NAME);
    if (exceededMaxFiles || hasUnfilledFile) {
      setWarningModalContent(
        exceededMaxFiles
          ? '1種類の書類で撮影・ファイル選択できる限度ファイル数を超えています。担当者にご連絡してください。'
          : '撮影またはファイルの選択を行ってから、追加をしてください。',
      );
    } else {
      const files = [...document.files];
      files.push({ fileId: `${document.documentId}_${new Date().getTime()}`, content: new File([], FILE_DEFAULT_NAME, { type: 'JPEG' }) });
      onFileChange(files);
    }
  };

  return (
    <div className="flex flex-col gap-5" key={`${document.documentId}`}>
      <StatusModal
        id="warning-file-number-exceeded-modal"
        title="画像を追加することができません"
        open={Boolean(warningModalContent)}
        setOpen={(isOpen) => !isOpen && setWarningModalContent('')}
        content={warningModalContent}
        buttons={[
          {
            text: 'OK',
          },
        ]}
      />
      <Heading as="h2">{header || document.documentId}</Heading>
      <Message status="warning" hasCloseButton={false}>
        {renderCustomTags(document.guidelines)}
      </Message>
      {hasError && <ErrorMessage message="書類の撮影・ファイル選択をしてください。" />}
      <div className="bg-statusInformationLight px-7 py-7 rounded-sm flex flex-col gap-5">
        {document.files.map(({ fileId, content }, idx) => {
          return (
            <FileUpload
              fileId={fileId}
              file={content}
              onFileChange={(file) => handleFileChange(idx, fileId, file)}
              key={fileId}
              receptionNo={receptionNo}
              index={idx}
              formId={document.documentId}
            />
          );
        })}
        {document.customerUploadRequired && (
          <Text color="danger" align="center">
            この書類はお客様ご自身で撮影いただく必要があります。お客様にタブレットをお渡しし、撮影から送信まで実施いただいてください。
          </Text>
        )}
      </div>
      <div>
        <Button icon={<AddIcon />} variant="outlined" type="button" onClick={handleFileAdd}>
          画像を追加する
        </Button>
      </div>

      <Text>※続けて裏面、次のページを撮影する方はこちらを押してください。</Text>
    </div>
  );
};

export default DocumentUpload;
